import React, { ElementRef, useState, useRef, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import * as luxon from "luxon";
import { DashboardUtils } from "../dashboard_utils";
import { createPortal } from "react-dom";

const ONE_MINUTE_MS = 1000 * 60;
const ONE_HOUR_MS = ONE_MINUTE_MS * 60;
const one_day_ms = ONE_HOUR_MS * 24;
const FIFTEEN_MIN_MS = ONE_MINUTE_MS * 15;
const SELECT_OPTION_INTERVAL = FIFTEEN_MIN_MS;

export function TimePicker(props: {
	initial_value_ms: number;
	updateFunc: (ms: number) => void;
	disabled: boolean;
	portal?: boolean;
}) {
	if (DashboardUtils.IsMobileOS()) {
		const locale = luxon.DateTime.local().locale;

		const value = msToTimeDropdownData(
			props.initial_value_ms,
			locale,
			true
		);

		return (
			<span className={props.disabled ? "input-disabled" : ""}>
				<input
					className="mobile-date-picker"
					type="time"
					value={value.label}
					onChange={(event) => {
						if (event.target?.valueAsDate) {
							props.updateFunc(
								event.target.valueAsDate.getTime()
							);
						}
					}}
					disabled={props.disabled}
				/>
			</span>
		);
	} else {
		return props.portal ? (
			<TimePickerPortal
				intial_value_ms={props.initial_value_ms}
				updateFunc={props.updateFunc}
				disabled={props.disabled}
			/>
		) : (
			<TimePickerDesktop
				intial_value_ms={props.initial_value_ms}
				updateFunc={props.updateFunc}
				disabled={props.disabled}
			/>
		);
	}
}

interface TimePickerFormProps {
	updateFunc: (ms: number) => void;
	intial_value_ms: number;
	disabled?: boolean;
}

interface TimePickerFormState {
	selected_time: TimeDropdownData;
	previous_option: TimeDropdownData;
	time_options: TimeDropdownData[];
	time_options_map: Map<number, TimeDropdownData>;
}

interface TimeDropdownData {
	value: number;
	label: string;
}

function localeUses24HourTime(langCode) {
	const meridien_value = new Intl.DateTimeFormat(langCode, {
		hour: "numeric",
	})
		.formatToParts(new Date())
		.find((part) => part.type === "dayPeriod")
		?.value?.toLowerCase();

	return meridien_value !== "am" && meridien_value !== "pm";
}

function getTimesToDisplay() {
	const results: Map<number, TimeDropdownData> = new Map();

	const locale = luxon.DateTime.local().locale;
	const uses_24hr_time = localeUses24HourTime(locale);

	let current_ms = 0;
	while (current_ms < one_day_ms) {
		const dropdown_data = msToTimeDropdownData(
			current_ms,
			locale,
			uses_24hr_time
		);

		results.set(dropdown_data.value, dropdown_data);

		current_ms += SELECT_OPTION_INTERVAL;
	}

	return results;
}

function padNumber(number: number) {
	if (number.toString().length === 1) {
		return `0${number}`;
	}
	return number.toString();
}

function msToTimeDropdownData(
	ms: number,
	locale: string,
	uses_24hr_time: boolean
): TimeDropdownData {
	const current_hour = Math.floor(ms / ONE_HOUR_MS);
	const current_minute = Math.floor(
		(ms - current_hour * ONE_HOUR_MS) / ONE_MINUTE_MS
	);

	const minute_string = padNumber(current_minute);

	let display_string: string;
	if (uses_24hr_time) {
		const hour_string = padNumber(current_hour);

		display_string = `${hour_string}:${minute_string}`;
	} else {
		const meridien = current_hour < 12 ? "am" : "pm";

		let hour_string: string;
		if (current_hour % 12 === 0) {
			hour_string = "12";
		} else {
			hour_string = padNumber(current_hour % 12);
		}

		display_string = `${hour_string}:${minute_string}${meridien}`;
	}

	return {
		value: ms,
		label: display_string,
	};
}

function parseTime(time_input: string): TimeDropdownData {
	const locale = luxon.DateTime.local().locale;
	const uses_24hr_time = localeUses24HourTime(locale);

	const time_components = time_input.split(":");

	const hour_raw = time_components[0];

	if (!hour_raw || hour_raw.length === 0 || hour_raw.length > 2) return;

	let parsed_hour = Number.parseInt(hour_raw);
	if (Number.isNaN(parsed_hour)) {
		return;
	}

	if (parsed_hour < 0) {
		return;
	}

	if (uses_24hr_time) {
		if (parsed_hour >= 24) return;
	} else {
		if (parsed_hour > 12 || parsed_hour === 0) return;
	}

	const minute_raw = time_components[1]?.substring(0, 2);
	if (!minute_raw || minute_raw.length < 2) {
		return;
	}

	const parsed_minute = Number.parseInt(minute_raw);
	if (Number.isNaN(parsed_minute)) {
		return;
	}

	if (parsed_minute < 0 || parsed_minute >= 60) {
		return;
	}
	if (uses_24hr_time) {
		const ms = parsed_hour * ONE_HOUR_MS + parsed_minute * ONE_MINUTE_MS;
		return msToTimeDropdownData(ms, locale, uses_24hr_time);
	} else {
		let meridien_raw = time_components[1]?.substring(2)?.toLowerCase();
		if (parsed_hour === 12) {
			parsed_hour = 0;
		}

		let ms: number;
		if (meridien_raw.includes("pm")) {
			ms =
				ONE_HOUR_MS * 12 +
				parsed_hour * ONE_HOUR_MS +
				parsed_minute * ONE_MINUTE_MS;
		} else {
			ms = parsed_hour * ONE_HOUR_MS + parsed_minute * ONE_MINUTE_MS;
		}

		return msToTimeDropdownData(ms, locale, uses_24hr_time);
	}
}

function getIndexToInsertNewTimeAt(
	new_time_ms: number,
	existing_options: TimeDropdownData[]
) {
	let start_index = 0;
	let end_index = existing_options.length - 1;

	while (start_index < end_index) {
		const current_index = Math.floor((start_index + end_index) / 2);

		const current_value = existing_options[current_index];
		if (current_value.value <= new_time_ms) {
			start_index = current_index + 1;
		} else {
			end_index = current_index - 1;
		}
	}

	const value = existing_options[start_index];

	return new_time_ms < value.value ? start_index : start_index + 1;
}

export function TimePickerPortal(props: TimePickerFormProps) {
	const containerRef = useRef<HTMLElement>(null);

	// Create the desktop picker but using a state handler to manage the selected time
	const [selectedTime, setSelectedTime] = useState<TimeDropdownData>(null);
	const times_to_display_map = getTimesToDisplay();
	const times_to_display_array: TimeDropdownData[] = [];
	times_to_display_map.forEach((val) => times_to_display_array.push(val));

	// Setup the time picker on first render
	useEffect(() => {
		if (props.intial_value_ms != null) {
			// get ms ignoring ms and seconds
			const true_ms =
				luxon.DateTime.fromMillis(props.intial_value_ms)
					.set({ second: 0, millisecond: 0 })
					.toMillis() % one_day_ms;

			// Find the closest option
			const locale = luxon.DateTime.local().locale;
			const uses_24hr_time = localeUses24HourTime(locale);
			const time_data = msToTimeDropdownData(
				true_ms,
				locale,
				uses_24hr_time
			);

			setSelectedTime(time_data);
		}
	}, [props.intial_value_ms]);

	const handleChange = (time: TimeDropdownData) => {
		if (time) {
			setSelectedTime(time);
			props.updateFunc(time.value);
		}
	};

	const handleMenuOpen = () => {
		// Scroll the selected element into view when menu opens
		setTimeout(() => {
			const selectedElement = document.querySelector(
				".sesh-dropdown__option--is-selected"
			);
			if (selectedElement) {
				selectedElement.scrollIntoView({
					behavior: "auto",
					block: "center",
				});
			}
		}, 0);
	};

	const isValidNewOption = (input_value: string) => {
		const parsed_time_data = parseTime(input_value);
		return (
			Boolean(parsed_time_data) &&
			!times_to_display_map.has(parsed_time_data.value)
		);
	};

	const formatCreateLabel = (input_value: string) => {
		return parseTime(input_value)?.label;
	};

	const onCreateOption = (input_value: string) => {
		const parsed_time_data = parseTime(input_value);
		if (!parsed_time_data) {
			return;
		}

		handleChange(parsed_time_data);
	};

	// Custom styles for react-select to use portaled menu
	const customStyles = {
		menu: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
		menuPortal: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
	};

	return (
		<span className="has-text-left" ref={containerRef}>
			<CreatableSelect
				isDisabled={props.disabled}
				inputId="select-input"
				classNamePrefix="sesh-dropdown"
				value={times_to_display_array.find(
					(val) => val.value === props.intial_value_ms
				)}
				isSearchable={true}
				options={times_to_display_array}
				onChange={handleChange}
				onMenuOpen={handleMenuOpen}
				isValidNewOption={isValidNewOption}
				onCreateOption={onCreateOption}
				formatCreateLabel={formatCreateLabel}
				styles={customStyles}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				menuPlacement="auto"
			/>
		</span>
	);
}

export class TimePickerDesktop extends React.Component<
	TimePickerFormProps,
	TimePickerFormState
> {
	constructor(props) {
		super(props);

		const times_to_display_map = getTimesToDisplay();

		const times_to_display_array: TimeDropdownData[] = [];
		times_to_display_map.forEach((val) => times_to_display_array.push(val));

		let initial_value: TimeDropdownData;
		if (this.props.intial_value_ms != null) {
			// get ms ignoring ms and seconds
			const true_ms =
				luxon.DateTime.fromMillis(this.props.intial_value_ms)
					.set({ second: 0, millisecond: 0 })
					.toMillis() % one_day_ms;

			if (times_to_display_map.has(true_ms)) {
				initial_value = times_to_display_map.get(true_ms);
			} else {
				const index_to_insert_at = getIndexToInsertNewTimeAt(
					true_ms,
					times_to_display_array
				);

				const locale = luxon.DateTime.local().locale;
				const uses_24hr_time = localeUses24HourTime(locale);

				initial_value = msToTimeDropdownData(
					true_ms,
					locale,
					uses_24hr_time
				);

				times_to_display_array.splice(
					index_to_insert_at,
					0,
					initial_value
				);
			}
		} else {
			initial_value = times_to_display_array[0];
		}

		this.state = {
			previous_option: undefined,
			selected_time: initial_value,
			time_options: times_to_display_array,
			time_options_map: times_to_display_map,
		};

		this.handleChange = this.handleChange.bind(this);
		this.onCreateOption = this.onCreateOption.bind(this);
		this.isValidNewOption = this.isValidNewOption.bind(this);
	}

	handleChange(date: TimeDropdownData) {
		this.setState({
			selected_time: date,
		});

		this.props.updateFunc(date.value);
	}

	formatCreateLabel(input_value: string) {
		return parseTime(input_value)?.label;
	}

	onCreateOption(input_value: string) {
		const parsed_time_data = parseTime(input_value);
		if (!parsed_time_data) {
			return;
		}

		this.setState((prevState) => {
			const index_to_insert_at = getIndexToInsertNewTimeAt(
				parsed_time_data.value,
				this.state.time_options
			);

			prevState.time_options.splice(
				index_to_insert_at,
				0,
				parsed_time_data
			);
			prevState.time_options_map.set(
				parsed_time_data.value,
				parsed_time_data
			);

			this.props.updateFunc(parsed_time_data.value);

			return { ...prevState, selected_time: parsed_time_data };
		});
	}

	isValidNewOption(input_value: string) {
		const parsed_time_data = parseTime(input_value);

		return (
			Boolean(parsed_time_data) &&
			!this.state.time_options_map.has(parsed_time_data.value)
		);
	}

	render() {
		return (
			<span className="has-text-left">
				<CreatableSelect
					isDisabled={this.props.disabled}
					inputId="select-input"
					classNamePrefix="sesh-dropdown"
					value={this.state.selected_time}
					isSearchable={true}
					options={this.state.time_options}
					onChange={this.handleChange}
					isValidNewOption={this.isValidNewOption}
					onCreateOption={this.onCreateOption}
					formatCreateLabel={this.formatCreateLabel}
				/>
			</span>
		);
	}
}
